import { useState, useRef, useEffect } from 'react';
import { PlayCircleOutlined, PauseCircleOutlined, LoadingOutlined } from '@ant-design/icons';

const AudioPlayer = ({ src, style }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleLoadedMetadata = () => {
      updateDuration();
      setIsLoading(false);
    };
    const handleDurationChange = () => updateDuration();
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      updateDuration();
    };
    const handleEnded = () => setIsPlaying(false);
    const handleCanPlayThrough = () => {
      updateDuration();
      setIsLoading(false);
    };
    const handleLoadedData = () => {
      updateDuration();
      setIsLoading(false);
    };
    const handleError = (e) => {
      console.error('Audio error:', e);
      setError('Failed to load audio');
      setIsLoading(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);

    // Try to get duration immediately if already loaded
    if (audio.readyState >= 1) {
      updateDuration();
    }

    // iOS Safari workaround: preload="metadata" doesn't always trigger metadata load
    // Force a small seek to trigger duration calculation
    const iOSWorkaround = setTimeout(() => {
      if (!duration && audio.readyState >= 1) {
        // Setting currentTime briefly can help iOS calculate duration
        const originalTime = audio.currentTime;
        audio.currentTime = 0.01;
        audio.currentTime = originalTime;
        updateDuration();
      }
    }, 500);

    return () => {
      clearTimeout(iOSWorkaround);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || error) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      // iOS Safari requires user gesture to load audio - force load first
      audio.load();

      // iOS Safari requires handling the play() promise
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Audio playback failed:', err);
            setIsPlaying(false);
            setIsLoading(false);
            // On iOS, try to play again after a small delay
            setTimeout(() => {
              audio.play().then(() => setIsPlaying(true)).catch(() => {});
            }, 100);
          });
      } else {
        setIsPlaying(true);
        setIsLoading(false);
      }
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audio.currentTime = percentage * duration;
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...style }}>
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        playsInline
        webkit-playsinline="true"
      />

      <button
        onClick={togglePlay}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'inherit'
        }}
      >
        {isLoading ? (
          <LoadingOutlined style={{ fontSize: '28px' }} spin />
        ) : isPlaying ? (
          <PauseCircleOutlined style={{ fontSize: '28px' }} />
        ) : (
          <PlayCircleOutlined style={{ fontSize: '28px' }} />
        )}
      </button>

      <div
        onClick={handleSeek}
        style={{
          flex: 1,
          height: '4px',
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderRadius: '2px',
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: 'currentColor',
            borderRadius: '2px',
            transition: 'width 0.1s'
          }}
        />
      </div>

      <span style={{ fontSize: '12px', minWidth: '70px', textAlign: 'right' }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  );
};

export default AudioPlayer;
